import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Meeting from "@/services/meeting";
import type { GetMeeting } from "@/dtos/Meeting.dto";

export interface MeetingsParams {
  organizationId: string;
  id?: string;
  page?: number;
  limit?: number;
  status?: string;
  contactId?: string;
  startDate?: string;
  endDate?: string;
  fname?: string;
  lname?: string;
  email?: string;
  mobileNumber?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
  bookedDate?: string[];
  meetingDate?: string[];
  bucketOptionId?: string;
  companyId?: string;
  tagId?: string;
  schedule?: string;
  offset?: number;
  contactOwnerId?: string | null;
  rsvpStatus?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useMeetings(params: Ref<MeetingsParams | null> | MeetingsParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["meetings", "disabled"];
    return [
      "meetings",
      p.organizationId,
      p.id || "",
      p.page || 1,
      p.limit || 10,
      p.status || "",
      p.contactId || "",
      p.contactOwnerId ?? "all",
      p.startDate || "",
      p.endDate || "",
      p.fname || "",
      p.lname || "",
      p.email || "",
      p.mobileNumber || "",
      p.jobTitle || "",
      p.company || "",
      p.industry || "",
      p.bucketOptionId || "",
      p.companyId || "",
      p.tagId || "",
      p.schedule || "",
      p.rsvpStatus || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetMeeting = {
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 25,
      };

      if (p.id) queryParams.id = p.id;
      if (p.status) queryParams.status = p.status;
      if (p.contactId) queryParams.contactId = p.contactId;
      if (p.contactOwnerId !== undefined) queryParams.contactOwnerId = p.contactOwnerId;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.fname) queryParams.fname = p.fname;
      if (p.lname) queryParams.lname = p.lname;
      if (p.email) queryParams.email = p.email;
      if (p.mobileNumber) queryParams.mobileNumber = p.mobileNumber;
      if (p.jobTitle) queryParams.jobTitle = p.jobTitle;
      if (p.company) queryParams.company = p.company;
      if (p.industry) queryParams.industry = p.industry;
      if (p.bookedDate) queryParams.bookedDate = p.bookedDate;
      if (p.meetingDate) queryParams.meetingDate = p.meetingDate;
      if (p.bucketOptionId) queryParams.bucketOptionId = p.bucketOptionId;
      if (p.companyId) queryParams.companyId = p.companyId;
      if (p.tagId) queryParams.tagId = p.tagId;
      if (p.schedule) queryParams.schedule = p.schedule;
      if (p.offset !== undefined) queryParams.offset = p.offset;
      if (p.rsvpStatus) queryParams.rsvpStatus = p.rsvpStatus;

      const result = await Meeting.getMeetings(queryParams);

      if (result.data.status) {
        return {
          data: result.data.data,
          metaData: result.data.metaData,
        };
      }

      throw new Error(result.data.msg || "Failed to load meetings");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
